/* Site-wide newsletter/promo popup -- see sections/popup.liquid for the
   markup + all the merchant-controlled settings this reads off data
   attributes (trigger type, delay/scroll thresholds, frequency, whether
   clicking the overlay closes it). Frequency gating and "already shown"
   state persist in localStorage/sessionStorage so the popup respects
   "once per session" / "once every N days" across page loads without
   needing a server-side cookie.

   IMPORTANT: in the theme editor, Shopify re-renders this section's
   HTML over AJAX (replacing the whole [data-popup] node) every time a
   setting changes -- including toggling "Preview this popup while
   editing". That's a brand-new DOM element with no listeners attached,
   so init() below is re-run on Shopify's `shopify:section:load` event
   (fired after that swap) in addition to the normal page-load run.
   Without this, checking the preview toggle would silently do nothing
   because the code that reacts to it already ran, once, before the
   toggle existed in the DOM. */
(function () {
  var STORAGE_KEY = "trulvl:popup:last-shown";
  var SESSION_KEY = "trulvl:popup:session";
  var SPIN_RESULT_KEY = "trulvl:popup:spin-result";
  /* Separate from STORAGE_KEY on purpose: STORAGE_KEY just tracks "when
     was this last seen" for the N-day between-shows cooldown, which is
     meant for someone who saw it and closed it without converting --
     they should get asked again later. CONVERTED_KEY is permanent (no
     expiry, no day count) and means "this visitor already submitted the
     form" -- once that's true they should never see this popup again,
     full stop, independent of whatever the Days setting is. */
  var CONVERTED_KEY = "trulvl:popup:converted";
  var WHEEL_COLORS = [
    "var(--clr-accent-orange)", "var(--clr-accent-blue)", "var(--clr-accent-pink)",
    "var(--clr-accent-purple)", "var(--clr-accent-green)", "var(--clr-ink)"
  ];
  var activeTimers = [];
  var activeIntervals = [];
  var activeListeners = [];

  function safeGet(storage, key) {
    try {
      return storage.getItem(key);
    } catch (e) {
      return null;
    }
  }
  function safeSet(storage, key, value) {
    try {
      storage.setItem(key, value);
    } catch (e) {}
  }

  function teardown() {
    activeTimers.forEach(window.clearTimeout);
    activeTimers = [];
    activeIntervals.forEach(window.clearInterval);
    activeIntervals = [];
    activeListeners.forEach(function (entry) {
      entry.target.removeEventListener(entry.type, entry.handler);
    });
    activeListeners = [];
  }

  function on(target, type, handler, opts) {
    target.addEventListener(type, handler, opts);
    activeListeners.push({ target: target, type: type, handler: handler });
  }

  /* ---- Countdown timer (works with any popup type) ---- */
  function initCountdown(popup) {
    var el = popup.querySelector("[data-popup-countdown]");
    if (!el) return;
    var target = new Date(el.getAttribute("data-countdown-end"));
    if (isNaN(target.getTime())) return;

    var expiredText = el.getAttribute("data-countdown-expired-text") || "";
    var daysEl = el.querySelector("[data-countdown-days]");
    var hoursEl = el.querySelector("[data-countdown-hours]");
    var minsEl = el.querySelector("[data-countdown-minutes]");
    var secsEl = el.querySelector("[data-countdown-seconds]");
    var unitsEl = el.querySelector(".popup__countdown-units");

    function pad(n) {
      return n < 10 ? "0" + n : String(n);
    }

    function tick() {
      var diff = target.getTime() - Date.now();
      if (diff <= 0) {
        if (unitsEl) unitsEl.remove();
        el.classList.add("is-expired");
        el.textContent = expiredText || el.textContent;
        window.clearInterval(intervalId);
        return;
      }
      var totalSeconds = Math.floor(diff / 1000);
      var days = Math.floor(totalSeconds / 86400);
      var hours = Math.floor((totalSeconds % 86400) / 3600);
      var mins = Math.floor((totalSeconds % 3600) / 60);
      var secs = totalSeconds % 60;
      if (daysEl) daysEl.textContent = pad(days);
      if (hoursEl) hoursEl.textContent = pad(hours);
      if (minsEl) minsEl.textContent = pad(mins);
      if (secsEl) secsEl.textContent = pad(secs);
    }

    tick();
    var intervalId = window.setInterval(tick, 1000);
    activeIntervals.push(intervalId);
  }

  /* ---- Spin to win ---- */
  function parseWheelSegments(raw) {
    return (raw || "")
      .split("\n")
      .map(function (line) { return line.trim(); })
      .filter(function (line) { return line.length > 0; })
      .map(function (line) {
        var parts = line.split("|").map(function (p) { return p.trim(); });
        var weight = parseInt(parts[2], 10);
        return {
          label: parts[0] || "",
          code: parts[1] || "",
          weight: isNaN(weight) || weight <= 0 ? 1 : weight
        };
      });
  }

  function initWheel(popup) {
    var wheel = popup.querySelector("[data-popup-wheel]");
    if (!wheel) return;
    var dial = wheel.querySelector("[data-wheel-dial]");
    var legend = wheel.querySelector("[data-wheel-legend]");
    var spinBtn = popup.querySelector("[data-spin-button]");
    var form = popup.querySelector(".popup__form");
    var segments = parseWheelSegments(wheel.getAttribute("data-wheel-segments"));
    if (!segments.length || !dial || !spinBtn || !form) return;

    var segAngle = 360 / segments.length;
    var gradientStops = segments.map(function (seg, i) {
      var color = WHEEL_COLORS[i % WHEEL_COLORS.length];
      return color + " " + (i * segAngle) + "deg " + ((i + 1) * segAngle) + "deg";
    });
    dial.style.background = "conic-gradient(" + gradientStops.join(", ") + ")";

    if (legend) {
      legend.innerHTML = "";
      segments.forEach(function (seg, i) {
        var li = document.createElement("li");
        li.className = "popup__wheel-legend-item";
        var swatch = document.createElement("span");
        swatch.className = "popup__wheel-legend-swatch";
        swatch.style.backgroundColor = WHEEL_COLORS[i % WHEEL_COLORS.length];
        li.appendChild(swatch);
        li.appendChild(document.createTextNode(seg.label));
        legend.appendChild(li);
      });
    }

    var spinning = false;
    var currentRotation = 0;

    on(spinBtn, "click", function (e) {
      e.preventDefault();
      if (spinning) return;
      if (form.reportValidity && !form.reportValidity()) return;

      spinning = true;
      spinBtn.disabled = true;
      wheel.classList.add("is-spinning");

      var totalWeight = segments.reduce(function (sum, s) { return sum + s.weight; }, 0);
      var roll = Math.random() * totalWeight;
      var winnerIndex = 0;
      var cumulative = 0;
      for (var i = 0; i < segments.length; i++) {
        cumulative += segments[i].weight;
        if (roll < cumulative) {
          winnerIndex = i;
          break;
        }
      }

      var winner = segments[winnerIndex];
      var segCenter = winnerIndex * segAngle + segAngle / 2;
      var extraSpins = 6 * 360;
      currentRotation += extraSpins + (360 - segCenter) - (currentRotation % 360);
      dial.style.transform = "rotate(" + currentRotation + "deg)";

      var finished = false;
      function finishSpin() {
        if (finished) return;
        finished = true;
        try {
          window.sessionStorage.setItem(SPIN_RESULT_KEY, JSON.stringify(winner));
        } catch (err) {}
        form.submit();
      }
      on(dial, "transitionend", finishSpin, { once: true });
      var t = window.setTimeout(finishSpin, 3600);
      activeTimers.push(t);
    });
  }

  /* After a spin-to-win submit reloads the page into the success state,
     fill in whichever prize sessionStorage says was won -- Liquid can't
     know this ahead of time since the wheel spin happens client-side. */
  function fillSpinResult(popup) {
    var resultEl = popup.querySelector("[data-spin-result]");
    if (!resultEl) return;
    var raw;
    try {
      raw = window.sessionStorage.getItem(SPIN_RESULT_KEY);
    } catch (err) {
      raw = null;
    }
    if (!raw) return;
    var winner;
    try {
      winner = JSON.parse(raw);
    } catch (err) {
      return;
    }
    if (winner && winner.code) {
      var codeEl = resultEl.querySelector("[data-spin-result-code]");
      var labelEl = resultEl.querySelector("[data-spin-result-label]");
      if (codeEl) codeEl.textContent = winner.code;
      if (labelEl) labelEl.textContent = winner.label;
      resultEl.hidden = false;
    }
    try {
      window.sessionStorage.removeItem(SPIN_RESULT_KEY);
    } catch (err) {}
  }

  function init(scope) {
    teardown();

    var popup = (scope || document).querySelector("[data-popup]");
    if (!popup) return;

    var overlay = popup.querySelector("[data-popup-overlay]");
    var closeBtn = popup.querySelector("[data-popup-close]");
    var panel = popup.querySelector("[data-popup-panel]");
    var isModal = popup.classList.contains("popup--modal");

    var forcePreview = popup.getAttribute("data-popup-force-preview") === "true";
    var designMode = popup.getAttribute("data-popup-design-mode") === "true";
    var trigger = popup.getAttribute("data-popup-trigger");
    var delaySeconds = parseFloat(popup.getAttribute("data-popup-delay")) || 5;
    var scrollPercent = parseFloat(popup.getAttribute("data-popup-scroll")) || 50;
    var frequency = popup.getAttribute("data-popup-frequency");
    var frequencyDays = parseFloat(popup.getAttribute("data-popup-frequency-days")) || 14;
    var closeOnOverlay = popup.getAttribute("data-popup-close-on-overlay") === "true";

    initCountdown(popup);
    initWheel(popup);
    fillSpinResult(popup);

    function hasBeenShownRecently() {
      if (frequency === "every_visit") return false;
      if (frequency === "once_per_session") {
        return safeGet(window.sessionStorage, SESSION_KEY) === "1";
      }
      if (frequency === "once_per_days") {
        var lastShown = parseInt(safeGet(window.localStorage, STORAGE_KEY), 10);
        if (!lastShown) return false;
        var elapsedDays = (Date.now() - lastShown) / (1000 * 60 * 60 * 24);
        return elapsedDays < frequencyDays;
      }
      return false;
    }

    function markShown() {
      safeSet(window.localStorage, STORAGE_KEY, String(Date.now()));
      safeSet(window.sessionStorage, SESSION_KEY, "1");
    }

    function hasConverted() {
      return safeGet(window.localStorage, CONVERTED_KEY) === "1";
    }

    function markConverted() {
      safeSet(window.localStorage, CONVERTED_KEY, "1");
    }

    function openPopup(skipMarkShown) {
      popup.classList.add("is-open");
      /* Intentionally NOT locking body scroll while the popup is open --
         a frozen background can make visitors feel trapped/stuck. The
         popup floats over a scrollable page instead, same as the nav
         dropdown's page-dim treatment. */
      if (!skipMarkShown) markShown();
      var firstInput = panel.querySelector("input");
      if (firstInput) {
        var t = window.setTimeout(function () {
          firstInput.focus();
        }, 300);
        activeTimers.push(t);
      }
    }

    function closePopup() {
      popup.classList.remove("is-open");
    }

    /* A plain (non-AJAX) form submit reloads the page -- if that reload is
       the result of a successful subscribe, sections/popup.liquid sets
       this flag inline before this script runs, so the popup reopens
       immediately showing the success state regardless of the normal
       trigger/frequency rules (the visitor just interacted with it on
       purpose, so there's no reason to gate showing them the result). */
    if (forcePreview) {
      /* "Preview this popup while editing" -- opens immediately, ignores
         every trigger/frequency rule below, and never touches
         localStorage/sessionStorage so it can't suppress (or
         fake-satisfy) the real frequency gating a visitor would
         experience once this checkbox is switched back off. */
      openPopup(true);
    } else if (window.__popupPostedSuccessfully) {
      /* Real conversion -- mark it permanently so this visitor never sees
         this popup again, distinct from (and stronger than) the N-day
         "closed without converting" cooldown below. Still shows the
         success state this one time, since they just submitted on
         purpose. */
      markConverted();
      openPopup();
    } else if (hasConverted()) {
      /* Already converted on a previous visit -- never re-arm, no matter
         what the trigger/frequency settings say. */
    } else if (designMode) {
      /* Inside the theme editor, with "Preview this popup while editing"
         OFF: don't arm any of the real trigger/frequency logic below at
         all. Without this, the live popup's own scroll/delay/exit-intent
         rules still fire in the editor same as they would for a real
         visitor -- and since the editor auto-scrolls the preview iframe
         whenever a merchant clicks a different section in the sidebar,
         a scroll-triggered popup could pop open mid-edit just from
         navigating around, with no way to tell it "not now" short of
         reloading. The explicit preview checkbox is the only way to see
         this popup while editing now -- normal visitor behavior is
         untouched, since `designMode` is only ever true inside the
         theme editor's own preview (`request.design_mode`). */
    } else if (!hasBeenShownRecently()) {
      if (trigger === "page_load") {
        openPopup();
      } else if (trigger === "delay") {
        var delayTimer = window.setTimeout(openPopup, delaySeconds * 1000);
        activeTimers.push(delayTimer);
      } else if (trigger === "scroll") {
        var scrollHandled = false;
        var onScroll = function () {
          if (scrollHandled) return;
          var scrollable = document.documentElement.scrollHeight - window.innerHeight;
          var scrolled = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 0;
          if (scrolled >= scrollPercent) {
            scrollHandled = true;
            openPopup();
          }
        };
        on(window, "scroll", onScroll, { passive: true });
      } else if (trigger === "exit_intent") {
        var exitHandled = false;
        var onExit = function (e) {
          if (exitHandled) return;
          // relatedTarget is null/undefined and clientY <= 0 when the cursor
          // leaves out the TOP of the viewport specifically (toward the
          // browser chrome/tab bar) -- the standard exit-intent heuristic,
          // as opposed to just moving between elements on the page.
          if (!e.relatedTarget && e.clientY <= 0) {
            exitHandled = true;
            openPopup();
          }
        };
        on(document, "mouseout", onExit);
      }
    }

    if (closeBtn) on(closeBtn, "click", closePopup);
    if (overlay && closeOnOverlay) on(overlay, "click", closePopup);
    on(document, "keydown", function (e) {
      if (e.key === "Escape" && popup.classList.contains("is-open")) closePopup();
    });
  }

  init();

  /* Re-run whenever the theme editor swaps this section's markup back in
     (settings change, block reorder, block added/removed, etc). `event.target`
     is the freshly-injected section wrapper, so scope the re-query to it. */
  document.addEventListener("shopify:section:load", function (event) {
    if (event.target && event.target.querySelector("[data-popup]")) {
      init(event.target);
    }
  });
})();
